/** Reports (docs/specs/reporting.md, R-02 … R-12, A-04 … A-07). */
export const reports = {
    title: "Reports",
    description: "Sales, stock, money, shifts, deliveries and customers. Every figure opens the records behind it.",
    open: "Open report",
    back: "All reports",
    groups: {
        sales: "Sales",
        stock: "Stock",
        finance: "Money",
        pos: "Point of sale",
        delivery: "Delivery",
        customers: "Customers",
    },
    names: {
        sales_summary: "Sales summary",
        product_profit: "Product profit",
        orders: "Orders",
        returns: "Returns",
        stock_valuation: "Stock valuation",
        stock_movements: "Stock movements",
        dead_stock: "Dead stock",
        low_stock: "Negative and low stock",
        profit_loss: "Profit and loss",
        vat: "VAT",
        receivables: "Receivables",
        payables: "Payables",
        expenses: "Expenses",
        transactions: "Money movements",
        shifts: "Shifts and Z-reports",
        price_variances: "Price variances",
        review_queue: "POS review queue",
        delivery_performance: "Delivery performance",
        customers: "Customers",
    },
    about: {
        sales_summary: "Sales by day, hour, product, category, brand, staff, channel, payment method or branch.",
        product_profit: "Revenue, cost and margin of each product.",
        orders: "Every sale in the period, with its net amount.",
        returns: "Goods that came back, with their value and refund.",
        stock_valuation: "Stock on hand at average cost, per branch.",
        stock_movements: "Where each item went and when.",
        dead_stock: "Stock that has not sold for a while.",
        low_stock: "What is below zero or at its reorder point.",
        profit_loss: "Sales − returns − discounts − cost − expenses = net profit.",
        vat: "VAT collected by month, less VAT given back on returns.",
        receivables: "What customers and couriers owe you.",
        payables: "What you owe suppliers, by age.",
        expenses: "Every expense in the period.",
        transactions: "Every taka in and out, as recorded.",
        shifts: "Cash expected, counted and the difference for each shift.",
        price_variances: "Offline sales at a price different from the current one.",
        review_queue: "Synced bills held or flagged for a check.",
        delivery_performance: "Delivered vs returned, by courier and district.",
        customers: "Top customers, their dues and how many came back.",
    },
    filters: {
        from: "From",
        to: "To",
        location: "Branch",
        allLocations: "All branches",
        channel: "Channel",
        allChannels: "All channels",
        groupBy: "Group by",
        days: "Unsold for (days)",
        status: "Status",
        run: "Show",
    },
    groupBy: {
        day: "Day",
        hour: "Hour of day",
        product: "Product",
        category: "Category",
        brand: "Brand",
        staff: "Staff",
        channel: "Channel",
        payment_method: "Payment method",
        location: "Branch",
    },
    reviewStatus: { open: "Open", resolved: "Resolved" },
    total: "Total",
    repeatRate: "Repeat customers: {{rate}}",
    empty: "Nothing in this period.",
    emptyBody: "Try a longer period or another branch.",
    truncated: "Showing the first {{count}} rows. Export the report for all of them.",
    rebuild: "Recompute summaries",
    rebuildDone: "Recomputing {{days}} days of summaries. Figures update in a minute.",
    export: {
        label: "Export",
        working: "Preparing the file…",
        started: "Preparing your file. It opens when ready, and the bell will have it too.",
        ready: "Your file is ready.",
        later: "Still preparing. You'll get a notification when it's ready.",
        failed: "The file could not be made. Try again.",
        title: "Report export",
        summary: "{{rows}} rows · made {{at}}",
        download: "Download {{format}}",
        formats: { xlsx: "Excel (.xlsx)", csv: "CSV", pdf: "PDF" },
    },
    columns: {
        date: "Date",
        orders: "Orders",
        gross_sales: "Gross sales",
        discounts: "Discounts",
        net_sales: "Net sales",
        returns: "Returns",
        cogs: "Cost of goods",
        gross_profit: "Gross profit",
        margin: "Margin",
        channel: "Channel",
        location: "Branch",
        product: "Product",
        category: "Category",
        brand: "Brand",
        sku: "SKU",
        qty: "Qty",
        sales: "Sales",
        net: "Net",
        hour: "Hour",
        staff: "Staff",
        payment_method: "Payment method",
        payments: "Payments",
        received: "Received",
        refunded: "Refunded",
        qty_sold: "Qty sold",
        qty_returned: "Qty returned",
        revenue: "Revenue",
        profit: "Profit",
        number: "No.",
        status: "Status",
        vat: "VAT",
        total: "Total",
        order: "Order",
        kind: "Kind",
        reason: "Reason",
        refund_method: "Refund by",
        value: "Value",
        refund: "Refund",
        products: "Products",
        negative: "Below zero",
        on_hand: "On hand",
        avg_cost: "Average cost",
        type: "Type",
        balance: "Balance",
        unit_cost: "Unit cost",
        last_sale: "Last sold",
        state: "State",
        reorder_point: "Reorder point",
        opened_by: "Opened by",
        opened_at: "Opened",
        closed_at: "Closed",
        float: "Float",
        cash_sales: "Cash sales",
        cash_refunds: "Cash refunds",
        cash_in: "Cash in",
        cash_out: "Cash out",
        expected: "Expected",
        counted: "Counted",
        variance: "Over / short",
        list_price: "Price on till",
        charged: "Charged",
        server_price: "Current price",
        difference: "Difference",
        reasons: "Reasons",
        detail: "Detail",
        courier: "Courier",
        district: "District",
        booked: "Booked",
        delivered: "Delivered",
        partial: "Partly delivered",
        returned: "Returned",
        open: "On the way",
        success_rate: "Success rate",
        cod: "COD",
        customer: "Customer",
        phone: "Phone",
        dues: "Dues",
        repeat_rate: "Repeat rate",
        line: "Line",
        name: "Name",
        amount: "Amount",
        period: "Period",
        taxable_sales: "Sales (ex VAT)",
        returns_vat: "VAT on returns",
        net_vat: "Net VAT",
        supplier: "Supplier",
        current: "0–30 days",
        days_31_60: "31–60 days",
        days_61_90: "61–90 days",
        over_90: "Over 90 days",
        advance: "Paid in advance",
        description: "Description",
        method: "Method",
        note: "Note",
    },
    lines: {
        gross_sales: "Gross sales",
        discounts: "Less: discounts",
        net_sales: "Net sales (ex VAT)",
        returns: "Less: returns",
        cogs: "Less: cost of goods sold",
        gross_profit: "Gross profit",
        expense: "Expense",
        total_expenses: "Less: expenses",
        courier_charges: "Less: courier charges",
        cash_over_short: "Cash over / short",
        net_profit: "Net profit",
    },
    values: {
        state: { negative: "Below zero", low: "Low" },
        receivable: { customer: "Customer dues", courier: "COD with courier" },
        variance: { stale_price: "Old price on till", override: "Price changed at till" },
    },
};
//# sourceMappingURL=reports.js.map